import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { 
  ArrowLeft, Upload, X, Plus, Trash2, Package, DollarSign, 
  Layers, Info, Heart, Award, Tag, ShoppingBag, CheckCircle, 
  AlertCircle, Image as ImageIcon, TrendingUp, Droplet, Sun, 
  Thermometer, Wind, Maximize, Clock, Shield, Truck, RotateCcw,
  Leaf, Flower2, TreePine, Sprout, Garden, Camera
} from 'lucide-react';
import axios from 'axios';

const AddProduct = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  // Plant Care Tips
  const [plantCareTips, setPlantCareTips] = useState([
    { title: 'Sunlight', description: 'Bright indirect sunlight', icon: '☀️' },
    { title: 'Watering', description: 'Water when soil feels dry', icon: '💧' },
    { title: 'Temperature', description: '65-85°F (18-29°C)', icon: '🌡️' }
  ]);

  // Additional Plant Details
  const [additionalDetails, setAdditionalDetails] = useState([
    'Pet-friendly plant', 'Air purifying', 'Low maintenance'
  ]);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    brand: user?.storeName || 'Paudhawala Nursery',
    price: '',
    originalPrice: '',
    stock: '',
    sku: '',
    rating: 4.7,
    reviewCount: 0,
    plantType: 'Indoor',
    sunlightRequirement: 'Partial Sun',
    wateringFrequency: 'Weekly',
    soilType: '',
    growthSpeed: 'Moderate',
    plantHeight: '',
    shortDescription: '',
    fullDescription: '',
    deliveryInfo: 'Usually dispatches within 24-48 hours.',
    returnPolicy: '7 days hassle-free return for damaged plants.',
    isActive: true,
    scientificName: '',
    potSize: '',
    bloomTime: '',
    difficulty: 'Easy',
    temperature: '',
    humidity: '',
    sellerId: user?._id || ''
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const uploadImageToCloudinary = async (file) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const uploadData = new FormData();
    uploadData.append('image', file);
    
    try {
      const { data } = await axios.post('/api/upload', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      if (data.success) return data.url;
      throw new Error('Image upload failed');
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      throw new Error('Failed to upload image');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size should be less than 2MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryUpload = (e) => {
    const files = Array.from(e.target.files);
    if (galleryFiles.length + files.length > 5) {
      setError('Maximum 5 gallery images allowed');
      return;
    }
    setGalleryFiles([...galleryFiles, ...files]);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setGalleryPreviews([...galleryPreviews, ...newPreviews]);
  };

  const removeGalleryImage = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  const addPlantCareTip = () => {
    setPlantCareTips([...plantCareTips, { title: '', description: '', icon: '🌿' }]);
  };

  const updatePlantCareTip = (index, field, value) => {
    const updated = [...plantCareTips];
    updated[index][field] = value;
    setPlantCareTips(updated);
  };

  const removePlantCareTip = (index) => {
    setPlantCareTips(plantCareTips.filter((_, i) => i !== index));
  };

  const addAdditionalDetail = () => {
    setAdditionalDetails([...additionalDetails, '']);
  };

  const updateAdditionalDetail = (index, value) => {
    const updated = [...additionalDetails];
    updated[index] = value;
    setAdditionalDetails(updated);
  };

  const removeAdditionalDetail = (index) => {
    setAdditionalDetails(additionalDetails.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.name || !formData.category || !formData.price || !formData.stock || !imageFile) {
      setError('Please fill all required fields and upload an image');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      setError('Uploading main image...');
      const imageUrl = await uploadImageToCloudinary(imageFile);
      
      let galleryUrls = [];
      if (galleryFiles.length > 0) {
        setError('Uploading gallery images...');
        galleryUrls = await Promise.all(galleryFiles.map(file => uploadImageToCloudinary(file)));
      }
      
      setError('Creating product...');
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        originalPrice: parseFloat(formData.originalPrice) || parseFloat(formData.price),
        stock: parseInt(formData.stock),
        rating: parseFloat(formData.rating),
        reviewCount: parseInt(formData.reviewCount),
        image: imageUrl,
        gallery: galleryUrls,
        plantCareTips: plantCareTips.filter(tip => tip.title && tip.description),
        additionalDetails: additionalDetails.filter(detail => detail.trim())
      };

      const { data } = await axios.post('/api/products', productData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (data.success) {
        setSuccess('Product added successfully! Redirecting...');
        setTimeout(() => navigate('/seller/products'), 2000);
      } else {
        setError(data.message || 'Failed to add product');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add product');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Info },
    { id: 'specs', label: 'Specifications', icon: Layers },
    { id: 'care', label: 'Care Tips', icon: Heart },
    { id: 'media', label: 'Media', icon: ImageIcon },
    { id: 'pricing', label: 'Pricing', icon: Tag }
  ];

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8f0e8 100%)', minHeight: '100vh' }}>
      <div className="container-fluid px-4 py-4">
        {/* Header */}
        <div className="mb-4">
          <div className="d-flex align-items-center gap-3 mb-3">
            <button 
              onClick={() => navigate('/seller/products')} 
              className="btn-back"
              type="button"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="fw-bold mb-0" style={{ fontSize: '1.75rem', color: '#1a472a' }}>🌱 Add New Plant</h1>
              <p className="text-muted mb-0">Create a beautiful product listing for your plant store</p>
            </div>
          </div>
          
          {/* Progress Steps */}
          <div className="progress-steps">
            {tabs.map((tab, index) => (
              <div 
                key={tab.id}
                className={`step ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="step-icon">
                  <tab.icon size={18} />
                </div>
                <span>{tab.label}</span>
                {index < tabs.length - 1 && <div className="step-line" />}
              </div>
            ))}
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-modern alert-error mb-4">
            <AlertCircle size={18} />
            <span>{error}</span>
            <button className="btn-close-small" onClick={() => setError('')}>×</button>
          </div>
        )}

        {success && (
          <div className="alert-modern alert-success mb-4">
            <CheckCircle size={18} />
            <span>{success}</span>
          </div>
        )}

        <form id="add-product-form" onSubmit={handleSubmit}>
          <div className="row g-4">
            {/* Left Column - Form Fields */}
            <div className="col-lg-8">
              {/* Basic Information Tab */}
              {activeTab === 'basic' && (
                <div className="form-card animate-fadeIn">
                  <div className="card-header-custom">
                    <Info size={20} />
                    <h5 className="fw-bold mb-0">Basic Information</h5>
                  </div>
                  <div className="card-body-custom">
                    <div className="row g-4">
                      <div className="col-12">
                        <label className="form-label-custom">Plant Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="form-control-custom"
                          placeholder="e.g., Monstera Deliciosa, Snake Plant"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Category <span className="text-danger">*</span></label>
                        <select name="category" value={formData.category} onChange={handleChange} className="form-control-custom">
                          <option value="">Select Category</option>
                          <option value="Indoor Plants">🏠 Indoor Plants</option>
                          <option value="Outdoor Plants">🌳 Outdoor Plants</option>
                          <option value="Flowering Plants">🌸 Flowering Plants</option>
                          <option value="Succulents">🌵 Succulents</option>
                          <option value="Cacti">🌵 Cacti</option>
                          <option value="Air Purifying Plants">🍃 Air Purifying Plants</option>
                          <option value="Herbs">🌿 Herbs</option>
                          <option value="Fruit Plants">🍎 Fruit Plants</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">SKU (Stock Keeping Unit)</label>
                        <input
                          type="text"
                          name="sku"
                          value={formData.sku}
                          onChange={handleChange}
                          className="form-control-custom"
                          placeholder="e.g., MON-001"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label-custom">Short Description <span className="text-danger">*</span></label>
                        <textarea
                          name="shortDescription"
                          value={formData.shortDescription}
                          onChange={handleChange}
                          className="form-control-custom"
                          rows="2"
                          placeholder="Brief description of the plant..."
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label-custom">Full Description <span className="text-danger">*</span></label>
                        <textarea
                          name="fullDescription"
                          value={formData.fullDescription}
                          onChange={handleChange}
                          className="form-control-custom"
                          rows="4"
                          placeholder="Detailed description about the plant..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Specifications Tab */}
              {activeTab === 'specs' && (
                <div className="form-card animate-fadeIn">
                  <div className="card-header-custom">
                    <Layers size={20} />
                    <h5 className="fw-bold mb-0">Plant Specifications</h5>
                  </div>
                  <div className="card-body-custom">
                    <div className="row g-4">
                      <div className="col-md-4">
                        <label className="form-label-custom">Plant Type</label>
                        <select name="plantType" value={formData.plantType} onChange={handleChange} className="form-control-custom">
                          <option value="Indoor">🏠 Indoor</option>
                          <option value="Outdoor">🌳 Outdoor</option>
                          <option value="Both">🔄 Both</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label-custom">Difficulty Level</label>
                        <select name="difficulty" value={formData.difficulty} onChange={handleChange} className="form-control-custom">
                          <option value="Easy">🌟 Easy</option>
                          <option value="Moderate">⚡ Moderate</option>
                          <option value="Hard">🔥 Hard</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label-custom">Growth Speed</label>
                        <select name="growthSpeed" value={formData.growthSpeed} onChange={handleChange} className="form-control-custom">
                          <option value="Slow">🐢 Slow</option>
                          <option value="Moderate">📈 Moderate</option>
                          <option value="Fast">🚀 Fast</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Scientific Name</label>
                        <input type="text" name="scientificName" value={formData.scientificName} onChange={handleChange} className="form-control-custom" placeholder="e.g., Monstera deliciosa" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Plant Height</label>
                        <input type="text" name="plantHeight" value={formData.plantHeight} onChange={handleChange} className="form-control-custom" placeholder="e.g., 12-24 inches" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Pot Size</label>
                        <input type="text" name="potSize" value={formData.potSize} onChange={handleChange} className="form-control-custom" placeholder="e.g., 6 inch" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Bloom Time</label>
                        <input type="text" name="bloomTime" value={formData.bloomTime} onChange={handleChange} className="form-control-custom" placeholder="e.g., Spring, Year-round" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Soil Type</label>
                        <input type="text" name="soilType" value={formData.soilType} onChange={handleChange} className="form-control-custom" placeholder="e.g., Well-draining" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Sunlight Requirement</label>
                        <select name="sunlightRequirement" value={formData.sunlightRequirement} onChange={handleChange} className="form-control-custom">
                          <option value="Full Sun">☀️ Full Sun</option>
                          <option value="Partial Sun">⛅ Partial Sun</option>
                          <option value="Low Light">🌙 Low Light</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Watering Frequency</label>
                        <select name="wateringFrequency" value={formData.wateringFrequency} onChange={handleChange} className="form-control-custom">
                          <option value="Daily">💧 Daily</option>
                          <option value="Twice a week">💧💧 Twice a week</option>
                          <option value="Weekly">💧 Weekly</option>
                          <option value="Bi-weekly">💧 Bi-weekly</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Temperature Range</label>
                        <input type="text" name="temperature" value={formData.temperature} onChange={handleChange} className="form-control-custom" placeholder="e.g., 65-85°F" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Humidity</label>
                        <input type="text" name="humidity" value={formData.humidity} onChange={handleChange} className="form-control-custom" placeholder="e.g., Moderate to high" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Plant Care Tips Tab */}
              {activeTab === 'care' && (
                <div className="form-card animate-fadeIn">
                  <div className="card-header-custom">
                    <Heart size={20} />
                    <h5 className="fw-bold mb-0">Plant Care Tips</h5>
                    <button type="button" className="btn-add-small ms-auto" onClick={addPlantCareTip}>
                      <Plus size={16} /> Add Tip
                    </button>
                  </div>
                  <div className="card-body-custom">
                    {plantCareTips.map((tip, index) => (
                      <div key={index} className="care-tip-item">
                        <div className="care-tip-header">
                          <span className="care-tip-icon">{tip.icon || '🌿'}</span>
                          <input
                            type="text"
                            className="care-tip-title"
                            placeholder="e.g., Sunlight"
                            value={tip.title}
                            onChange={(e) => updatePlantCareTip(index, 'title', e.target.value)}
                          />
                          <button type="button" className="btn-icon-danger" onClick={() => removePlantCareTip(index)}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <input
                          type="text"
                          className="form-control-custom mt-2"
                          placeholder="Care instruction"
                          value={tip.description}
                          onChange={(e) => updatePlantCareTip(index, 'description', e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Media Tab */}
              {activeTab === 'media' && (
                <div className="form-card animate-fadeIn">
                  <div className="card-header-custom">
                    <ImageIcon size={20} />
                    <h5 className="fw-bold mb-0">Product Media</h5>
                  </div>
                  <div className="card-body-custom">
                    <label className="form-label-custom">Main Image <span className="text-danger">*</span></label>
                    <div className="upload-area" onClick={() => document.getElementById('mainImageInput').click()}>
                      {imagePreview ? (
                        <div className="upload-preview">
                          <img src={imagePreview} alt="Preview" />
                          <button type="button" className="btn-remove-image" onClick={(e) => {
                            e.stopPropagation();
                            setImagePreview(null);
                            setImageFile(null);
                          }}>
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <Camera size={40} />
                          <p>Click to upload main image</p>
                          <span>PNG, JPG up to 2MB</span>
                        </div>
                      )}
                      <input id="mainImageInput" type="file" accept="image/*" onChange={handleImageUpload} className="d-none" />
                    </div>

                    <label className="form-label-custom mt-4">Gallery Photos (Optional)</label>
                    <div className="gallery-upload-area" onClick={() => document.getElementById('galleryInput').click()}>
                      <Plus size={32} />
                      <p>Add more photos</p>
                      <span>{galleryFiles.length}/5 photos</span>
                      <input id="galleryInput" type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="d-none" disabled={galleryFiles.length >= 5} />
                    </div>
                    {galleryPreviews.length > 0 && (
                      <div className="gallery-preview-grid mt-3">
                        {galleryPreviews.map((preview, index) => (
                          <div key={index} className="gallery-item">
                            <img src={preview} alt={`Gallery ${index + 1}`} />
                            <button type="button" onClick={() => removeGalleryImage(index)}>
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === 'pricing' && (
                <div className="form-card animate-fadeIn">
                  <div className="card-header-custom">
                    <Tag size={20} />
                    <h5 className="fw-bold mb-0">Pricing & Inventory</h5>
                  </div>
                  <div className="card-body-custom">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label-custom">Price (₹) <span className="text-danger">*</span></label>
                        <input type="number" name="price" value={formData.price} onChange={handleChange} className="form-control-custom" placeholder="0.00" step="0.01" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Original Price (MRP)</label>
                        <input type="number" name="originalPrice" value={formData.originalPrice} onChange={handleChange} className="form-control-custom" placeholder="0.00" step="0.01" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Stock Quantity <span className="text-danger">*</span></label>
                        <input type="number" name="stock" value={formData.stock} onChange={handleChange} className="form-control-custom" placeholder="Number of plants available" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Rating (0-5)</label>
                        <input type="number" name="rating" value={formData.rating} onChange={handleChange} className="form-control-custom" step="0.1" min="0" max="5" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label-custom">Review Count</label>
                        <input type="number" name="reviewCount" value={formData.reviewCount} onChange={handleChange} className="form-control-custom" placeholder="0" />
                      </div>
                      <div className="col-12">
                        <label className="form-label-custom">Special Features</label>
                        {additionalDetails.map((detail, index) => (
                          <div key={index} className="detail-item mb-2">
                            <input
                              type="text"
                              className="form-control-custom"
                              placeholder="e.g., Pet-friendly, Air purifying"
                              value={detail}
                              onChange={(e) => updateAdditionalDetail(index, e.target.value)}
                            />
                            <button type="button" className="btn-icon-danger ms-2" onClick={() => removeAdditionalDetail(index)}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                        <button type="button" className="btn-add-small mt-2" onClick={addAdditionalDetail}>
                          <Plus size={16} /> Add Feature
                        </button>
                      </div>
                      <div className="col-12">
                        <div className="form-check">
                          <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} className="form-check-input" id="isActive" />
                          <label className="form-check-label" htmlFor="isActive">Product Active (visible in shop)</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Preview & Actions */}
            <div className="col-lg-4">
              <div className="form-card sticky-top" style={{ top: '20px' }}>
                <div className="card-header-custom">
                  <ShoppingBag size={20} />
                  <h5 className="fw-bold mb-0">Publish Product</h5>
                </div>
                <div className="card-body-custom">
                  {/* Preview Section */}
                  <div className="preview-section mb-4">
                    <h6 className="fw-semibold mb-3">Preview</h6>
                    <div className="preview-card">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" />
                      ) : (
                        <div className="preview-placeholder">
                          <ImageIcon size={48} />
                          <span>No image selected</span>
                        </div>
                      )}
                      <div className="preview-info">
                        <h6>{formData.name || 'Plant Name'}</h6>
                        <p className="price">₹{formData.price || '0'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Information */}
                  <div className="info-section mb-4">
                    <h6 className="fw-semibold mb-3">Delivery Information</h6>
                    <div className="info-item">
                      <Truck size={16} />
                      <span>{formData.deliveryInfo}</span>
                    </div>
                    <div className="info-item mt-2">
                      <RotateCcw size={16} />
                      <span>{formData.returnPolicy}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <button 
                    type="submit"
                    form="add-product-form"
                    className="btn-publish w-100"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <Package size={18} className="me-2" />
                        Publish Product
                      </>
                    )}
                  </button>
                  
                  <button 
                    type="button"
                    onClick={() => navigate('/seller/products')}
                    className="btn-cancel w-100 mt-3"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>

        <style>{`.btn-back {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 12px;
  color: #1a472a;
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;
  cursor: pointer;
}

.btn-back:hover {
  background: #f8fafc;
  transform: translateX(-4px);
}

.progress-steps {
  display: flex;
  align-items: center;
  gap: 0;
  background: white;
  padding: 12px 24px;
  border-radius: 50px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 40px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.step .step-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.step.active .step-icon {
  background: #00a651;
  color: white;
}

.step span {
  font-size: 0.85rem;
  font-weight: 500;
  color: #64748b;
}

.step.active span {
  color: #00a651;
}

.step-line {
  width: 40px;
  height: 2px;
  background: #e2e8f0;
  margin: 0 8px;
}

.form-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  overflow: hidden;
  border: 1px solid #e8ecef;
}

.card-header-custom {
  padding: 18px 24px;
  background: #fafcfc;
  border-bottom: 1px solid #e8ecef;
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-header-custom svg {
  color: #00a651;
}

.card-body-custom {
  padding: 24px;
}

.form-label-custom {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 6px;
  color: #1a2a3a;
  display: block;
}

.form-control-custom {
  width: 100%;
  padding: 10px 14px;
  font-size: 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
  background: white;
}

.form-control-custom:focus {
  outline: none;
  border-color: #00a651;
  box-shadow: 0 0 0 3px rgba(0,166,81,0.1);
}

.btn-publish {
  background: #00a651;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-publish:hover:not(:disabled) {
  background: #008a44;
  transform: translateY(-2px);
}

.btn-publish:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-cancel {
  background: #f1f5f9;
  color: #475569;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-cancel:hover {
  background: #e2e8f0;
}

.upload-area,
.gallery-upload-area {
  border: 2px dashed #cbd5e1;
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafcfc;
}

.upload-area:hover,
.gallery-upload-area:hover {
  border-color: #00a651;
  background: #f0fdf4;
}

.upload-preview {
  position: relative;
}

.upload-preview img {
  max-height: 200px;
  width: 100%;
  object-fit: contain;
  border-radius: 12px;
}

.btn-remove-image {
  position: absolute;
  top: -10px;
  right: -10px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.gallery-preview-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.gallery-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-item button {
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(0,0,0,0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.care-tip-item {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.care-tip-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.care-tip-icon {
  font-size: 20px;
}

.care-tip-title {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
}

.detail-item {
  display: flex;
  align-items: center;
}

.btn-add-small {
  background: none;
  border: none;
  color: #00a651;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 20px;
  transition: all 0.2s ease;
}

.btn-add-small:hover {
  background: #f0fdf4;
}

.btn-icon-danger {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
}

.btn-icon-danger:hover {
  background: #fef2f2;
}

.preview-card {
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.preview-card img {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.preview-placeholder {
  height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  gap: 8px;
}

.preview-info {
  padding: 12px;
}

.preview-info h6 {
  margin: 0 0 4px;
  font-size: 0.9rem;
}

.preview-info .price {
  color: #00a651;
  font-weight: 600;
  margin: 0;
}

.info-section {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
  color: #475569;
}

.alert-modern {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: 16px;
  font-size: 0.85rem;
}

.alert-error {
  background: #fef2f2;
  border: 1px solid #fee2e2;
  color: #dc2626;
}

.alert-success {
  background: #f0fdf4;
  border: 1px solid #dcfce7;
  color: #16a34a;
}

.btn-close-small {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: inherit;
}

.sticky-top {
  position: sticky;
  top: 20px;
}

@media (max-width: 768px) {
  .progress-steps {
    flex-wrap: wrap;
    border-radius: 20px;
    gap: 8px;
  }

  .step-line {
    display: none;
  }

  .card-header-custom,
  .card-body-custom {
    padding: 16px;
  }
}`}</style>
      </div>
    </div>
  );
};

export default AddProduct;